//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import * as pinterestPinsApi from '@apis/pinterest-pins/pinterestPinsApi';
import {
  IOrganicPinConfig,
  UniversalScheduledPostConfigType,
} from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { isScheduledOrganicPinConfig } from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import {
  createDraftPosts,
  updateDraftPost,
} from '@apis/universal-posts/draft-posts/universalDraftPostsApi';
import { ICreateDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import * as universalScheduledPostsApi from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import { deleteScheduledPosts } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import {
  ICreateUpdateScheduledPost,
  ISearchScheduledPostsRequest,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { UniversalPostingGroupName } from '@apis/universal-posts/universalPostsApiModel';
import { getStandardPostDetails } from '@apis/universal-posts/universalPostsUtils';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import PinId from '@models/PinId';
import PinterestBoardId from '@models/PinterestBoardId';
import PinterestBoardSectionId from '@models/PinterestBoardSectionId';
import PinterestPinId from '@models/PinterestPinId';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import {
  PostDetailsDraftErrorMessages,
  PostDetailsScheduleErrorMessages,
} from '@pinterest/components/post-details/pinterestPostDetailsUIConstants';
import {
  scheduleBulkPinsSucceededToast,
  scheduleSinglePinSucceededToast,
} from '@pinterest/components/schedule/pinterestScheduleToasts';
import {
  IComparablePinterestPostDetails,
  IPinterestPostDetailsPin,
  IPinterestPostDetailsState,
  IStaticPinterestPostDetails,
  MAX_PIN_DESCRIPTION_LENGTH,
  MAX_PIN_TITLE_LENGTH,
  PinPostingIntervalValue,
  PinterestPostDetailsBulkScheduleValidationError,
  PinterestPostDetailsSchedulingStep,
  PinterestPostDetailsValidationError,
  PinterestScheduleType,
} from '@pinterest/redux/post-details/pinterestPostDetailsModel';
import {
  generateEmptyPin,
  validateBulkPinBoardSelect,
  validateBulkPinMediaAndDetails,
  validateBulkScheduleDetails,
  validateSinglePinPostDetails,
} from '@pinterest/redux/post-details/pinterestPostDetailsUtils';
import { getBulkPinScheduledTimes } from '@pinterest/redux/post-details/selectors/getBulkPinScheduledTimes';
import { getBulkScheduleDetails } from '@pinterest/redux/post-details/selectors/getBulkScheduleDetails';
import { getCurrentPin } from '@pinterest/redux/post-details/selectors/getCurrentPin';
import { getOrderedPostDetailsPins } from '@pinterest/redux/post-details/selectors/getOrderedPostDetailsPins';
import {
  getDraftId,
  getIsDraftingPin,
  getIsSchedulingPin,
} from '@pinterest/redux/post-details/selectors/getPinterestPostDetails';
import { getPinterestUserId } from '@pinterest/redux/post-details/selectors/getPinterestUserId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import {
  getMediaItemByCuid,
  getMediaItemsByCuid,
  updateMediaItems,
} from '@redux/schedule-media/scheduleMediaSlice';
import { getClientId } from '@redux/selectors/getClientId';
import {
  addSwapDraftToPostFailureToast,
  addSwapDraftToPostSuccessToast,
  addSwapPostToDraftFailureToast,
  addSwapPostToDraftSuccessToast,
} from '@universal-scheduling/redux/drafts/toasts/universalDraftsSwapToast';
import {
  deleteDraftPost,
  fetchDraftById,
} from '@universal-scheduling/redux/drafts/universalDraftsSlice';
import { move } from '@utils/arrayUtils';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { WeekDayValue } from '@utils/moment';
import normalize, { Normalized } from '@utils/normalize';
import { RequestError } from '@utils/sessionFetch';
import createUUID from '@utils/uuid';

import { RoutableStateName } from 'app/top-level-states';

const defaultPin = generateEmptyPin();
const defaultPinsNormalized = normalize([defaultPin], (p) => p.id);

const defaultComparablePostDetails = {
  pins: defaultPinsNormalized,
  bulkScheduleDetails: {
    isBulkBoardSelectEnabled: false,
    daysOfWeek: [],
    validationErrors: PojoSet.empty(),
  },
};

const initialState: IPinterestPostDetailsState = {
  currentPostDetails: defaultComparablePostDetails,
  initialPostDetails: defaultComparablePostDetails,
  currentPinId: defaultPin.id,
  scheduleType: PinterestScheduleType.Single,
  schedulingStep: PinterestPostDetailsSchedulingStep.MediaDetails,
  previewCuidIndex: 0,
  schedulingEntryPoint: 'pinterest.schedule.calendar',

  errors: {},
  flags: {
    hasSetPostDetails: false,
    isDraftingPin: false,
    isFetchingSelectedMedia: false,
    isInitializingBulkPinMedia: false,
    isSchedulingPin: false,
  },
};

function getCurrentSelectedPin(state: IPinterestPostDetailsState) {
  // We should always be able to find the current pin by id here but fallback to the first pin in case.
  return getPinWithFallback(
    Normalized.getById(state.currentPostDetails.pins, state.currentPinId) ??
      Normalized.getOrdered(state.currentPostDetails.pins)[0],
  );
}

const getPinWithFallback = (
  pin?: IPinterestPostDetailsPin,
): IPinterestPostDetailsPin => {
  return pin ?? defaultPin;
};

const { actions, reducer } = createSlice({
  name: SliceNames.PinterestPostDetails,
  initialState,
  reducers: {
    setInitialPostDetails(
      state,
      action: PayloadAction<{
        staticPostDetails: IStaticPinterestPostDetails;
        comparablePostDetails: IComparablePinterestPostDetails;
        setInitialPostDetailsError?: string;
      }>,
    ) {
      const { draftId, failedAttempts, postId, postStatus } =
        action.payload.staticPostDetails;
      state.draftId = draftId;
      state.postId = postId;
      state.postStatus = postStatus;
      state.failedAttempts = failedAttempts;
      state.currentPostDetails = action.payload.comparablePostDetails;
      state.initialPostDetails = action.payload.comparablePostDetails;
      state.currentPinId = getPinWithFallback(
        Normalized.getOrdered(action.payload.comparablePostDetails.pins)[0],
      ).id;
      state.errors.setInitialPostDetailsError =
        action.payload.setInitialPostDetailsError;
      state.flags.hasSetPostDetails = true;
    },
    setInitialBulkPinPostDetails(
      state,
      action: PayloadAction<{
        startScheduledTime?: Date;
      }>,
    ) {
      state.scheduleType = PinterestScheduleType.Bulk;
      state.flags.hasSetPostDetails = true;
      state.currentPostDetails.bulkScheduleDetails.startScheduledTime =
        action.payload.startScheduledTime;
    },
    requestSelectedMedia(
      state,
      action: PayloadAction<{ isCreatingBulkPins?: boolean }>,
    ) {
      if (action.payload.isCreatingBulkPins) {
        state.flags.isInitializingBulkPinMedia = true;
      } else {
        state.flags.isFetchingSelectedMedia = true;
      }

      const currentPin = getCurrentSelectedPin(state);
      currentPin.validationErrors = PojoSet.difference(
        currentPin.validationErrors,
        PojoSet.from([PinterestPostDetailsValidationError.NoMedia]),
      );
    },
    receiveSelectedMediaSucceeded(state) {
      state.flags.isFetchingSelectedMedia = false;
      state.flags.isInitializingBulkPinMedia = false;
    },
    receiveSelectedMediaFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingSelectedMedia = false;
      state.flags.isInitializingBulkPinMedia = false;
      state.errors.fetchSelectedMediaError = action.payload;
    },
    addCurrentPinCuids(state, action: PayloadAction<Cuid[]>) {
      getCurrentSelectedPin(state).cuids.push(...action.payload);
    },
    removeSelectedCuid(state, action: PayloadAction<Cuid>) {
      const mediaIndex = getCurrentSelectedPin(state).cuids.findIndex(
        (cuid) => cuid === action.payload,
      );
      if (!isDefined(mediaIndex)) {
        return;
      }

      getCurrentSelectedPin(state).cuids.splice(mediaIndex, 1);
      const cuidLength = getCurrentSelectedPin(state).cuids.length;
      if (cuidLength === 0) {
        state.previewCuidIndex = 0;
      } else if (
        mediaIndex < state.previewCuidIndex ||
        state.previewCuidIndex >= cuidLength
      ) {
        state.previewCuidIndex = state.previewCuidIndex - 1;
      }

      const currentPin = getCurrentSelectedPin(state);

      if (!isDefined(currentPin)) {
        return;
      }

      currentPin.validationErrors = PojoSet.difference(
        currentPin.validationErrors,
        PojoSet.from([
          PinterestPostDetailsValidationError.VideoTooLong,
          PinterestPostDetailsValidationError.MediaTooSmall,
        ]),
      );
    },
    addPins(
      state,
      action: PayloadAction<Normalized<IPinterestPostDetailsPin, PinId>>,
    ) {
      state.currentPostDetails.pins = Normalized.addItems(
        state.currentPostDetails.pins,
        action.payload,
      );
    },
    addNewEmptyPin(state) {
      const bulkSelectedBoardId =
        state.currentPostDetails.bulkScheduleDetails.bulkBoardSelectBoardId;
      const bulkSelectedBoardSectionId =
        state.currentPostDetails.bulkScheduleDetails
          .bulkBoardSelectBoardSectionId;
      const newEmptyPin = {
        ...generateEmptyPin(),
        boardId: bulkSelectedBoardId,
        boardSection: bulkSelectedBoardSectionId,
      };

      state.currentPostDetails.pins = Normalized.addItem(
        state.currentPostDetails.pins,
        newEmptyPin.id,
        newEmptyPin,
      );
      state.currentPinId = newEmptyPin.id;
      state.previewCuidIndex = 0;
    },
    removePin(state, action: PayloadAction<PinId>) {
      const pinId = action.payload;

      // Update current pin id if removing current pin
      if (pinId === state.currentPinId) {
        const orderedIds = state.currentPostDetails.pins.orderedIds;
        const currIndex = orderedIds.findIndex(
          (id) => id === state.currentPinId,
        );
        const newPinIdIndex = currIndex === 0 ? 1 : currIndex - 1;
        state.currentPinId =
          state.currentPostDetails.pins.orderedIds[newPinIdIndex] ??
          defaultPin.id;
        state.previewCuidIndex = 0;
      }

      // If removing last remaining pin, reset pins state
      if (state.currentPostDetails.pins.orderedIds.length === 1) {
        const newPin = generateEmptyPin();
        state.currentPostDetails.pins = normalize([newPin], (p) => p.id);
        state.currentPinId = newPin.id;
      } else {
        state.currentPostDetails.pins = Normalized.removeItem(
          state.currentPostDetails.pins,
          pinId,
        );
      }
    },
    setTitle(state, action: PayloadAction<string>) {
      const currentPin = getCurrentSelectedPin(state);
      currentPin.title = action.payload;

      if (action.payload.length < MAX_PIN_TITLE_LENGTH) {
        currentPin.validationErrors = PojoSet.difference(
          currentPin.validationErrors,
          PojoSet.from([PinterestPostDetailsValidationError.TitleTooLong]),
        );
      }
    },
    setDescription(state, action: PayloadAction<string>) {
      const currentPin = getCurrentSelectedPin(state);
      currentPin.description = action.payload;

      let errorsToReset = PojoSet.empty();

      if (action.payload.length < MAX_PIN_DESCRIPTION_LENGTH) {
        errorsToReset = PojoSet.add(
          errorsToReset,
          PinterestPostDetailsValidationError.DescriptionTooLong,
        );
      }

      currentPin.validationErrors = PojoSet.difference(
        currentPin.validationErrors,
        errorsToReset,
      );
    },
    setDestinationLink(state, action: PayloadAction<string>) {
      const currentPin = getCurrentSelectedPin(state);
      currentPin.destinationLink = action.payload ? action.payload : undefined;

      currentPin.validationErrors = PojoSet.difference(
        currentPin.validationErrors,
        PojoSet.from([
          PinterestPostDetailsValidationError.InvalidDestinationLink,
        ]),
      );
    },
    setBoardId(
      state,
      action: PayloadAction<{
        boardId: PinterestBoardId;
        boardSectionId?: PinterestBoardSectionId;
      }>,
    ) {
      const currentPin = getCurrentSelectedPin(state);
      currentPin.boardId = action.payload.boardId;
      currentPin.boardSection = action.payload.boardSectionId;

      currentPin.validationErrors = PojoSet.difference(
        currentPin.validationErrors,
        PojoSet.from([PinterestPostDetailsValidationError.NoBoard]),
      );
    },
    toggleBulkBoardSelect(state: IPinterestPostDetailsState) {
      const bulkScheduleDetails = state.currentPostDetails.bulkScheduleDetails;
      bulkScheduleDetails.isBulkBoardSelectEnabled =
        !bulkScheduleDetails.isBulkBoardSelectEnabled;

      // If untoggled, clear the bulk selected board and validation errors.
      // If toggled, clear all individual board selections.
      if (!bulkScheduleDetails.isBulkBoardSelectEnabled) {
        state.currentPostDetails.bulkScheduleDetails.bulkBoardSelectBoardId =
          undefined;
        state.currentPostDetails.bulkScheduleDetails.bulkBoardSelectBoardSectionId =
          undefined;
        bulkScheduleDetails.validationErrors = PojoSet.difference(
          bulkScheduleDetails.validationErrors,
          PojoSet.from([
            PinterestPostDetailsBulkScheduleValidationError.NoBulkBoardSelected,
          ]),
        );
      } else {
        state.currentPostDetails.pins.byId = PojoMap.map(
          state.currentPostDetails.pins.byId,
          (pin) => {
            return {
              ...pin,
              boardId: undefined,
              boardSection: undefined,
            };
          },
        );
      }
    },
    setAllPinBoardIds(
      state: IPinterestPostDetailsState,
      action: PayloadAction<{
        boardId: PinterestBoardId;
        boardSection?: PinterestBoardSectionId;
      }>,
    ) {
      const bulkScheduleDetails = state.currentPostDetails.bulkScheduleDetails;
      bulkScheduleDetails.bulkBoardSelectBoardId = action.payload.boardId;
      bulkScheduleDetails.bulkBoardSelectBoardSectionId =
        action.payload.boardSection;
      state.currentPostDetails.pins.byId = PojoMap.map(
        state.currentPostDetails.pins.byId,
        (pin) => {
          return {
            ...pin,
            boardId: action.payload.boardId,
            boardSection: action.payload.boardSection,
          };
        },
      );

      bulkScheduleDetails.validationErrors = PojoSet.difference(
        bulkScheduleDetails.validationErrors,
        PojoSet.from([
          PinterestPostDetailsBulkScheduleValidationError.NoBulkBoardSelected,
        ]),
      );
    },
    setScheduledTime(state, action: PayloadAction<Date>) {
      const currentPin = getCurrentSelectedPin(state);
      currentPin.scheduledTime = action.payload;

      currentPin.validationErrors = PojoSet.difference(
        currentPin.validationErrors,
        PojoSet.from([
          PinterestPostDetailsValidationError.NoPostTime,
          PinterestPostDetailsValidationError.PostTimeInPast,
        ]),
      );
    },
    setCurrentPinId(state, action: PayloadAction<PinId>) {
      state.currentPinId = action.payload;
      state.previewCuidIndex = 0;
    },
    setSchedulingStep(
      state,
      action: PayloadAction<PinterestPostDetailsSchedulingStep>,
    ) {
      state.schedulingStep = action.payload;
    },
    setSchedulingEntryPoint(state, action: PayloadAction<RoutableStateName>) {
      state.schedulingEntryPoint = action.payload;
    },
    setBulkScheduleDaysOfWeek(state, action: PayloadAction<WeekDayValue[]>) {
      const bulkScheduleDetails = state.currentPostDetails.bulkScheduleDetails;
      bulkScheduleDetails.daysOfWeek = action.payload;

      bulkScheduleDetails.validationErrors = PojoSet.difference(
        bulkScheduleDetails.validationErrors,
        PojoSet.from([
          PinterestPostDetailsBulkScheduleValidationError.NoDayOfWeek,
        ]),
      );
    },
    setBulkSchedulePinsPerDay(state, action: PayloadAction<number>) {
      const bulkScheduleDetails = state.currentPostDetails.bulkScheduleDetails;
      bulkScheduleDetails.pinsPerDay = action.payload;

      bulkScheduleDetails.validationErrors = PojoSet.difference(
        bulkScheduleDetails.validationErrors,
        PojoSet.from([
          PinterestPostDetailsBulkScheduleValidationError.NoPinsPerDay,
        ]),
      );
    },
    setBulkSchedulePostingInterval(
      state,
      action: PayloadAction<PinPostingIntervalValue>,
    ) {
      const bulkScheduleDetails = state.currentPostDetails.bulkScheduleDetails;
      bulkScheduleDetails.postingInterval = action.payload;

      bulkScheduleDetails.validationErrors = PojoSet.difference(
        bulkScheduleDetails.validationErrors,
        PojoSet.from([
          PinterestPostDetailsBulkScheduleValidationError.NoPostingInterval,
        ]),
      );
    },
    setBulkScheduleStartScheduledTime(state, action: PayloadAction<Date>) {
      const bulkScheduleDetails = state.currentPostDetails.bulkScheduleDetails;
      bulkScheduleDetails.startScheduledTime = action.payload;

      bulkScheduleDetails.validationErrors = PojoSet.difference(
        bulkScheduleDetails.validationErrors,
        PojoSet.from([
          PinterestPostDetailsBulkScheduleValidationError.NoStartPostTime,
        ]),
      );
    },
    requestSaveScheduledPin(state) {
      state.flags.isSchedulingPin = true;
      state.errors.scheduleFailureError = undefined;
      state.errors.draftFailureError = undefined;
    },
    saveScheduledPinSucceeded(state) {
      state.flags.isSchedulingPin = false;
    },
    saveScheduledPinFailed(state, action: PayloadAction<string>) {
      state.flags.isSchedulingPin = false;
      state.errors.scheduleFailureError = action.payload;
    },
    requestSaveDraftPin(state) {
      state.flags.isDraftingPin = true;
      state.errors.draftFailureError = undefined;
      state.errors.scheduleFailureError = undefined;
    },
    receiveSaveDraftPinSucceeded(state) {
      state.flags.isDraftingPin = false;
    },
    receiveSaveDraftPinFailed(
      state,
      action: PayloadAction<{
        draftFailureError: string;
      }>,
    ) {
      state.flags.isDraftingPin = false;
      state.errors.draftFailureError = action.payload.draftFailureError;
    },
    setSinglePinValidationErrors(
      state,
      action: PayloadAction<{
        formErrors: PojoSet<PinterestPostDetailsValidationError>;
        invalidCuids: PojoSet<Cuid>;
      }>,
    ) {
      getCurrentSelectedPin(state).validationErrors = action.payload.formErrors;
      getCurrentSelectedPin(state).invalidCuids = action.payload.invalidCuids;
    },
    setBulkPinValidationErrors(
      state: IPinterestPostDetailsState,
      action: PayloadAction<
        PojoMap<
          PinId,
          {
            formErrors: PojoSet<PinterestPostDetailsValidationError>;
            invalidCuids: PojoSet<Cuid>;
          }
        >
      >,
    ) {
      state.currentPostDetails.pins.orderedIds.forEach((id) => {
        const pin = Normalized.getById(state.currentPostDetails.pins, id);
        const errors = PojoMap.get(action.payload, id);

        if (pin && errors) {
          pin.validationErrors = errors.formErrors;
          pin.invalidCuids = errors.invalidCuids;
        }
      });
    },
    setBulkScheduleValidationErrors(
      state,
      action: PayloadAction<
        PojoSet<PinterestPostDetailsBulkScheduleValidationError>
      >,
    ) {
      state.currentPostDetails.bulkScheduleDetails.validationErrors =
        action.payload;
    },
    setPreviewCuidIndex(state, action: PayloadAction<number>) {
      state.previewCuidIndex = action.payload;
    },
    updatePinPosition(
      state,
      {
        payload: { currIndex, newIndex },
      }: PayloadAction<{
        currIndex: number;
        newIndex: number;
      }>,
    ) {
      const reorderedPinId =
        state.currentPostDetails.pins.orderedIds[currIndex];

      state.currentPostDetails.pins.orderedIds = move(
        state.currentPostDetails.pins.orderedIds,
        currIndex,
        newIndex,
      );

      state.currentPinId = reorderedPinId ?? state.currentPinId;
    },
    resetPostDetails() {
      return initialState;
    },
  },
});

export const {
  addCurrentPinCuids: _testOnlyAddCurrentPinCuids,
  addNewEmptyPin,
  addPins: _testOnlyAddPins,
  removePin,
  removeSelectedCuid,
  resetPostDetails,
  setAllPinBoardIds,
  setBoardId,
  setBulkScheduleDaysOfWeek,
  setBulkSchedulePinsPerDay,
  setBulkSchedulePostingInterval,
  setBulkScheduleStartScheduledTime,
  setCurrentPinId,
  setDescription,
  setDestinationLink,
  setInitialBulkPinPostDetails: _testOnlySetInitialBulkPinPostDetails,
  setInitialPostDetails: _testOnlySetInitialPostDetails,
  setPreviewCuidIndex,
  setScheduledTime,
  setSchedulingEntryPoint,
  setSchedulingStep,

  setSinglePinValidationErrors,
  setTitle,
  toggleBulkBoardSelect,
  updatePinPosition,
} = actions;

/**
 * Sets up post details page for scheduling a single pin.
 *
 * Initalizes scheduled time and/or target cuid, along with the description
 * and destination link if it exists on the target cuid's network item.
 */
const setupCreateSinglePinPost = createThunk(
  (scheduledTime?: Date, targetCuid?: Cuid) => async (dispatch) => {
    const newEmptyPin = generateEmptyPin();

    const mediaItem = targetCuid
      ? await dispatch(getMediaItemByCuid(targetCuid))
      : undefined;

    const networkItem = mediaItem?.networkItem;

    const initializedPin = {
      ...newEmptyPin,
      cuids: targetCuid ? [targetCuid] : [],
      description: networkItem?.caption,
      destinationLink: networkItem?.networkData?.mediaImportData?.source,
      scheduledTime,
    };

    const comparablePostDetails: IComparablePinterestPostDetails = {
      ...defaultComparablePostDetails,
      pins: normalize([initializedPin], (p) => p.id),
    };

    dispatch(
      actions.setInitialPostDetails({
        staticPostDetails: {},
        comparablePostDetails,
      }),
    );
  },
);

/**
 * Sets up post details page for bulk scheduling.
 *
 * If bulk scheduling from the "Select Schedule Type" page, the pins are already set by the fetchAndSetSelectedCuids action
 * which is called after the user selects media from the content drawer.
 *
 * If we have target cuids (i.e. scheduling from Curalate Save Button), fetch media and
 * create pins from the selected cuids.
 */
const setupCreateBulkPinPost = createThunk(
  (startScheduledTime?: Date, targetCuids?: Cuid[]) => async (dispatch) => {
    if (targetCuids?.length) {
      const mediaItems = await dispatch(
        fetchMediaItemsForSelectedCuids(targetCuids, true),
      );
      dispatch(setBulkPinsFromSelectedCuids(targetCuids, mediaItems));
    }

    dispatch(actions.setInitialBulkPinPostDetails({ startScheduledTime }));
  },
);

export const setupCreatePost = createThunk(
  (
    scheduleType: PinterestScheduleType,
    postTimeMs?: number,
    targetCuids?: Cuid[],
  ) =>
    async (dispatch) => {
      const scheduledTime = postTimeMs ? new Date(postTimeMs) : undefined;

      try {
        if (scheduleType === PinterestScheduleType.Bulk) {
          await dispatch(setupCreateBulkPinPost(scheduledTime, targetCuids));
        } else {
          await dispatch(
            setupCreateSinglePinPost(scheduledTime, targetCuids?.[0]),
          );
        }
      } catch (_e) {
        dispatch(
          actions.setInitialPostDetails({
            staticPostDetails: {},
            comparablePostDetails: defaultComparablePostDetails,
            setInitialPostDetailsError: `Failed to set up new post. Please try again or contact Bazaarvoice Support.`,
          }),
        );
      }
    },
);

/**
 * Sets up edit post details from a previously scheduled pin.
 */
export const setupEditPost = createThunk(
  (postId: PostableId) => async (dispatch) => {
    try {
      const scheduledPin =
        await universalScheduledPostsApi.getScheduledPost(postId);
      const config = scheduledPin.config;

      if (isScheduledOrganicPinConfig(config)) {
        const { boardId, boardSection, cuids, description, title, url } =
          config;

        if (PojoMap.size(scheduledPin.mediaItems) > 0) {
          dispatch(updateMediaItems(scheduledPin.mediaItems));
        }

        const staticPostDetails: IStaticPinterestPostDetails = {
          postId,
          postStatus: scheduledPin.status,
          failedAttempts: scheduledPin.failedAttempts,
        };

        const postDetailsPin: IPinterestPostDetailsPin = {
          ...generateEmptyPin(),

          boardId,
          boardSection,
          cuids,
          description,
          destinationLink: url,
          scheduledTime: scheduledPin.scheduledTime,
          originalScheduledTime: undefined,
          title,
        };

        const comparablePostDetails: IComparablePinterestPostDetails = {
          ...defaultComparablePostDetails,
          pins: normalize([postDetailsPin], (p) => p.id),
        };

        dispatch(
          actions.setInitialPostDetails({
            staticPostDetails,
            comparablePostDetails,
          }),
        );
      } else {
        throw Error();
      }
    } catch (e) {
      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails: {},
          comparablePostDetails: defaultComparablePostDetails,
          setInitialPostDetailsError: `Failed to load in post #${postId}`,
        }),
      );
    }
  },
);

export const setupEditDraftPin = createThunk(
  (draftId: DraftId) => async (dispatch) => {
    try {
      const draftPost = await dispatch(fetchDraftById(draftId));

      if (!isDefined(draftPost)) {
        throw new Error(`Could not load draft post: ${draftId}`);
      }

      if (!isScheduledOrganicPinConfig(draftPost.config)) {
        throw new Error(
          `Missing Pinterest Organic Pin config for draft post: ${draftId}`,
        );
      }

      const postDetailsPin: IPinterestPostDetailsPin = {
        id: PinId(createUUID()),
        validationErrors: PojoSet.empty(),
        invalidCuids: PojoSet.empty(),
        boardId: draftPost.config.boardId,
        boardSection: draftPost.config.boardSection,
        cuids: draftPost.config.cuids,
        description: draftPost.config.description,
        destinationLink: draftPost.config.url,
        title: draftPost.config.title,
        scheduledTime: draftPost.scheduledTime,
      };

      const normalizedPins = normalize<IPinterestPostDetailsPin, PinId>(
        [postDetailsPin],
        (p) => p.id,
      );
      const comparablePostDetails: IComparablePinterestPostDetails = {
        pins: normalizedPins,
        bulkScheduleDetails: defaultComparablePostDetails.bulkScheduleDetails,
      };

      const staticPostDetails: IStaticPinterestPostDetails = {
        draftId,
      };
      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails,
          comparablePostDetails,
        }),
      );
    } catch (e) {
      const errMsg =
        getRequestErrorMessage(e) ??
        PostDetailsDraftErrorMessages.RequestLoadDraftPinFailed;

      console.error(errMsg);

      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails: {},
          comparablePostDetails: defaultComparablePostDetails,
          setInitialPostDetailsError: `Failed to load existing Pinterest draft post: ${draftId}`,
        }),
      );
    }
  },
);

export const setupPage = createThunk(
  ({
    copiedDraftId,
    copiedPinId,
    copiedScheduledPostId,
    draftId,
    postId,
    postTimeMs,
    scheduleType,
    targetCuids,
  }: {
    postId: PostableId | undefined;
    copiedScheduledPostId: PostableId | undefined;
    copiedPinId: PinterestPinId | undefined;
    copiedDraftId: DraftId | undefined;
    draftId: DraftId | undefined;
    scheduleType: PinterestScheduleType;
    postTimeMs: number | undefined;
    targetCuids: Cuid[] | undefined;
  }) =>
    (dispatch, getState) => {
      if (postId) {
        void dispatch(setupEditPost(postId));
      } else if (isDefined(copiedScheduledPostId) || isDefined(copiedDraftId)) {
        void dispatch(
          setupCopyScheduledPost(copiedScheduledPostId, copiedDraftId),
        );
      } else if (copiedPinId) {
        void dispatch(setupCopyPinFromPinterest(copiedPinId));
      } else if (isDefined(draftId)) {
        void dispatch(setupEditDraftPin(draftId));
      } else {
        void dispatch(setupCreatePost(scheduleType, postTimeMs, targetCuids));
      }
    },
);

/**
 * Sets up the repin post details from a previously scheduled post or pin.
 */
export const setupCopyScheduledPost = createThunk(
  (copiedScheduledPostId?: PostableId, copiedDraftId?: DraftId) =>
    async (dispatch) => {
      try {
        let scheduledPost;
        if (isDefined(copiedScheduledPostId)) {
          scheduledPost = await universalScheduledPostsApi.getScheduledPost(
            copiedScheduledPostId,
          );
        } else if (isDefined(copiedDraftId)) {
          scheduledPost = await dispatch(fetchDraftById(copiedDraftId));
        } else {
          throw new Error(`Cannot copy without post or draft id`);
        }

        if (!isDefined(scheduledPost)) {
          return;
        }

        if (PojoMap.size(scheduledPost.mediaItems) > 0) {
          dispatch(updateMediaItems(scheduledPost.mediaItems));
        }

        const config = scheduledPost.config;
        let postDetailsPin: IPinterestPostDetailsPin;

        if (isScheduledOrganicPinConfig(config)) {
          const { boardId, boardSection, cuids, description, title, url } =
            config;

          postDetailsPin = {
            ...generateEmptyPin(),

            boardId,
            boardSection,
            cuids,
            description,
            destinationLink: url,
            scheduledTime: undefined,
            originalScheduledTime: scheduledPost.scheduledTime,
            title,
          };
        } else {
          const { caption, cuids, scheduledTime } =
            getStandardPostDetails(scheduledPost);

          postDetailsPin = {
            ...generateEmptyPin(),

            cuids: cuids ?? [],
            description: caption,
            scheduledTime: undefined,
            originalScheduledTime: scheduledTime,
          };
        }

        const comparablePostDetails: IComparablePinterestPostDetails = {
          ...defaultComparablePostDetails,
          pins: normalize([postDetailsPin], (p) => p.id),
        };
        dispatch(
          actions.setInitialPostDetails({
            staticPostDetails: {},
            comparablePostDetails,
          }),
        );
      } catch (e) {
        dispatch(
          actions.setInitialPostDetails({
            staticPostDetails: {},
            comparablePostDetails: defaultComparablePostDetails,
            setInitialPostDetailsError: `Failed to load in post #${copiedScheduledPostId}`,
          }),
        );
      }
    },
);

/**
 * Sets up the repin post details from a PinterestPinId. Either finds the associated scheduled pin id or gets info directly from Pinterest
 */
export const setupCopyPinFromPinterest = createThunk(
  (pinId: PinterestPinId) => async (dispatch) => {
    try {
      // First try to find an associated scheduledPost and copy that
      const requestParams: ISearchScheduledPostsRequest = {
        limit: 1,
        groups: [UniversalPostingGroupName.PinterestOrganicPinGroup],
        status: [PostableStatus.Posted],
        postedItemIds: [pinId.toString()],
      };

      const scheduledPin = (
        await universalScheduledPostsApi.searchScheduledPosts(requestParams)
      ).data[0];

      if (isDefined(scheduledPin) && isDefined(scheduledPin.id)) {
        await dispatch(setupCopyScheduledPost(scheduledPin.id));
        return;
      }

      // If the pin wasn't scheduled then copy what we can get from Pinterest
      const postedPin = await pinterestPinsApi.getPin(pinId);

      if (isDefined(postedPin.mediaItem)) {
        dispatch(
          updateMediaItems(
            PojoMap.fromEntries([
              [postedPin.mediaItem.cuid, postedPin.mediaItem],
            ]),
          ),
        );
      }

      const cuid = postedPin.mediaItem?.cuid;

      const postDetailsPin: IPinterestPostDetailsPin = {
        ...generateEmptyPin(),

        boardId: postedPin.pin?.board?.id,
        cuids: isDefined(cuid) ? [cuid] : [],
        description: postedPin.pin?.description,
        destinationLink: postedPin.pin?.link,
        originalScheduledTime: postedPin.pin?.createdAt
          ? new Date(Date.parse(postedPin.pin.createdAt))
          : undefined,
      };

      const comparablePostDetails: IComparablePinterestPostDetails = {
        ...defaultComparablePostDetails,
        pins: normalize([postDetailsPin], (p) => p.id),
      };

      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails: {},
          comparablePostDetails,
        }),
      );
    } catch (e) {
      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails: {},
          comparablePostDetails: defaultComparablePostDetails,
          setInitialPostDetailsError: `Failed to load in pin #${pinId}`,
        }),
      );
    }
  },
);

export const fetchAndSetSelectedCuids = createThunk(
  (contentDrawerItems: IContentLibraryItem[], isCreatingBulkPins?: boolean) =>
    async (dispatch, getState) => {
      const clientId = getClientId(getState());
      const newCuids = contentDrawerItems.map((item) =>
        Cuid.build(item, clientId),
      );

      const dedupedCuids = Array.from(new Set(newCuids));

      const mediaItems = await dispatch(
        fetchMediaItemsForSelectedCuids(dedupedCuids, isCreatingBulkPins),
      );

      if (isCreatingBulkPins) {
        dispatch(setBulkPinsFromSelectedCuids(dedupedCuids, mediaItems));
      } else {
        const rootCuid = dedupedCuids[0];
        if (rootCuid) {
          const rootMediaItem = mediaItems && PojoMap.get(mediaItems, rootCuid);
          dispatch(setCuidsForSinglePin(dedupedCuids, rootMediaItem));
        }
      }
    },
);

/*
 * Loads the hydrated MLS media items associated with the given cuids
 * into the schedule media store and returns the media items.
 */
const fetchMediaItemsForSelectedCuids = createThunk(
  (cuids: Cuid[], isCreatingBulkPins?: boolean) => async (dispatch) => {
    dispatch(actions.requestSelectedMedia({ isCreatingBulkPins }));

    try {
      const mediaItems = await dispatch(getMediaItemsByCuid(cuids));

      dispatch(actions.receiveSelectedMediaSucceeded());
      return mediaItems;
    } catch (e) {
      const msg = (e as RequestError).message;
      dispatch(actions.receiveSelectedMediaFailed(msg));
    }
  },
);

/**
 * Set the cuids for a single pin when adding media from the content drawer.
 *
 * Add pin cuids and description/destination link if this is the first cuid.
 * We use the cuid as a key to re-render the Smart Input, so it should be set last to ensure
 * the fields have already been set.
 */
export const setCuidsForSinglePin = createThunk(
  (cuids: Cuid[], rootMediaItem?: IMlsMediaItem) => (dispatch, getState) => {
    const pin = getCurrentPin(getState());
    const rootNetworkItem = rootMediaItem?.networkItem;
    const description = rootNetworkItem?.caption;
    const destinationLink =
      rootNetworkItem?.networkData?.mediaImportData?.source;

    if (pin.cuids.length === 0) {
      if (description) {
        dispatch(actions.setDescription(description));
      }

      if (destinationLink) {
        dispatch(actions.setDestinationLink(destinationLink));
      }
    }

    dispatch(actions.addCurrentPinCuids(cuids));
  },
);

/**
 * Create bulk pins from the selected cuids with each cuid as a separate pin.
 *
 * The cuids are selected from the content drawer from the Select page
 * before navigating to the Post Details page. Also autopopulates the description
 * and destination link associated with the selected cuids.
 */
export const setBulkPinsFromSelectedCuids = createThunk(
  (cuids: Cuid[], mediaItems?: PojoMap<Cuid, IMlsMediaItem>) => (dispatch) => {
    const [firstCuid, ...remainingCuids] = cuids;

    if (!firstCuid) {
      return;
    }

    const firstMediaItem = mediaItems && PojoMap.get(mediaItems, firstCuid);
    dispatch(setCuidsForSinglePin([firstCuid], firstMediaItem));

    const pins: IPinterestPostDetailsPin[] = remainingCuids.map((cuid) => {
      const mediaItem = mediaItems && PojoMap.get(mediaItems, cuid);
      const networkItem = mediaItem?.networkItem;
      return {
        ...generateEmptyPin(),
        cuids: [cuid],
        destinationLink: networkItem?.networkData?.mediaImportData?.source,
        description: networkItem?.caption,
      };
    });

    const normalizedPins = normalize(pins, (p) => p.id);

    dispatch(actions.addPins(normalizedPins));
  },
);

export const validateAndSchedulePin = createThunk(
  (options: { shouldShowToast: boolean } = { shouldShowToast: true }) =>
    async (dispatch): Promise<boolean> => {
      const isPinValid = dispatch(validateSinglePinDetails());
      if (isPinValid) {
        const didScheduleSuccessfully = await dispatch(
          saveScheduledSinglePin(options.shouldShowToast),
        );
        return didScheduleSuccessfully;
      }

      return false;
    },
);

export const saveDraftSinglePin = createThunk(
  () =>
    async (dispatch, getState): Promise<boolean> => {
      dispatch(actions.requestSaveDraftPin());

      const state = getState();
      const pinterestUserId = getPinterestUserId(state);
      const pin = getCurrentPin(state);
      const draftId = getDraftId(state);

      try {
        if (!pinterestUserId) {
          throw new Error('Pinterest User not found');
        }

        const config: IOrganicPinConfig = {
          boardId: pin.boardId,
          boardSection: pin.boardSection,
          cuids: pin.cuids,
          description: pin.description,
          title: pin.title,
          url: pin.destinationLink,
          '@type': UniversalScheduledPostConfigType.PinterestOrganicPin,
        };
        const request: ICreateDraftPost = {
          config,
          postingEntityId: pinterestUserId,
          scheduledTime: pin.scheduledTime,
        };

        if (isDefined(draftId)) {
          await updateDraftPost(draftId, config, request.scheduledTime);
        } else {
          await createDraftPosts([request]);
        }
        dispatch(actions.receiveSaveDraftPinSucceeded());

        return true;
      } catch (e) {
        dispatch(
          actions.receiveSaveDraftPinFailed({
            draftFailureError:
              PostDetailsDraftErrorMessages.RequestSaveDraftPinFailed,
          }),
        );
        return false;
      }
    },
);

const validateSinglePinDetails = createThunk(
  () =>
    (dispatch, getState): boolean => {
      const pin = getCurrentPin(getState());
      const scheduleMedia = getState().scheduleMedia.mediaItems;
      const associatedMediaItems = pin.cuids
        .map((c) => PojoMap.get(scheduleMedia, c))
        .filter(isDefined);

      const errorInfo = validateSinglePinPostDetails(pin, associatedMediaItems);

      const hasValidationErrors = PojoSet.size(errorInfo.formErrors) > 0;
      if (hasValidationErrors) {
        dispatch(actions.setSinglePinValidationErrors(errorInfo));
        return false;
      }

      return true;
    },
);

const saveScheduledSinglePin = createThunk(
  (shouldShowToast: boolean) =>
    async (dispatch, getState): Promise<boolean> => {
      dispatch(actions.requestSaveScheduledPin());

      const { postId } = getState().pinterest.postDetails;
      const pinterestUserId = getState().pinterest.user.user?.id;
      const pin = getCurrentPin(getState());

      if (!pin.scheduledTime || !pin.boardId || !pinterestUserId) {
        return false;
      }

      const req: ICreateUpdateScheduledPost = {
        scheduledTime: pin.scheduledTime,
        postingEntityId: pinterestUserId,
        config: {
          boardId: pin.boardId,
          boardSection: pin.boardSection,
          cuids: pin.cuids,
          description: pin.description,
          title: pin.title,
          url: pin.destinationLink,
          '@type': UniversalScheduledPostConfigType.PinterestOrganicPin,
        } as IOrganicPinConfig,
      };

      const schedulePinPromise = postId
        ? universalScheduledPostsApi.updateScheduledPost(postId, req)
        : universalScheduledPostsApi.createScheduledPosts([req]);

      try {
        await schedulePinPromise;
        dispatch(actions.saveScheduledPinSucceeded());
        if (shouldShowToast) {
          dispatch(
            toastNotificationsActions.addToastNotification(
              scheduleSinglePinSucceededToast(pin.scheduledTime),
            ),
          );
        }
        return true;
      } catch (_e) {
        dispatch(
          actions.saveScheduledPinFailed(
            PostDetailsScheduleErrorMessages.RequestSchedulePinFailed,
          ),
        );
        return false;
      }
    },
);

export const validateBulkPinDetailsAndNavigate = createThunk(
  () =>
    (dispatch, getState): void => {
      const pins = getOrderedPostDetailsPins(getState());
      const { bulkBoardSelectBoardId, isBulkBoardSelectEnabled } =
        getBulkScheduleDetails(getState());
      const scheduleMedia = getState().scheduleMedia.mediaItems;

      // Validate media and details for each pin
      const pinFormErrorsByPinId = validateBulkPinMediaAndDetails(
        pins,
        scheduleMedia,
      );

      dispatch(actions.setBulkPinValidationErrors(pinFormErrorsByPinId));

      const formErrors = PojoMap.values(pinFormErrorsByPinId).map(
        (e) => e.formErrors,
      );
      const areAllPinsValid = formErrors.every((e) => PojoSet.size(e) === 0);

      // Validate the bulk board select form section
      const bulkBoardSelectError = validateBulkPinBoardSelect(
        isBulkBoardSelectEnabled,
        bulkBoardSelectBoardId,
      );

      dispatch(actions.setBulkScheduleValidationErrors(bulkBoardSelectError));

      if (areAllPinsValid && PojoSet.size(bulkBoardSelectError) === 0) {
        dispatch(
          actions.setSchedulingStep(
            PinterestPostDetailsSchedulingStep.ScheduleDetails,
          ),
        );
      }
    },
);

export const validateBulkScheduleDetailsAndSchedulePins = createThunk(
  () =>
    async (dispatch): Promise<boolean> => {
      const areBulkScheduleDetailsValid = dispatch(
        validateBulkPinsScheduleDetails(),
      );
      if (areBulkScheduleDetailsValid) {
        const didScheduleSuccessfully = await dispatch(saveBulkScheduledPins());
        return didScheduleSuccessfully;
      }

      return false;
    },
);

const validateBulkPinsScheduleDetails = createThunk(
  () =>
    (dispatch, getState): boolean => {
      const bulkScheduleDetails = getBulkScheduleDetails(getState());
      const bulkScheduleFormErrors =
        validateBulkScheduleDetails(bulkScheduleDetails);

      dispatch(actions.setBulkScheduleValidationErrors(bulkScheduleFormErrors));

      return PojoSet.size(bulkScheduleFormErrors) === 0;
    },
);

const saveBulkScheduledPins = createThunk(
  () =>
    async (dispatch, getState): Promise<boolean> => {
      dispatch(actions.requestSaveScheduledPin());

      const pinterestUserId = getState().pinterest.user.user?.id;
      const pins = getOrderedPostDetailsPins(getState());

      const bulkScheduledTimes = getBulkPinScheduledTimes(getState());
      const startScheduledTime =
        bulkScheduledTimes &&
        PojoMap.get(bulkScheduledTimes, getPinWithFallback(pins[0]).id);

      if (!bulkScheduledTimes || !startScheduledTime || !pinterestUserId) {
        return false;
      }

      const pinRequests = pins
        .map((pin) => {
          const scheduledTime = PojoMap.get(bulkScheduledTimes, pin.id);
          if (!scheduledTime || !pin.boardId) {
            return undefined;
          }

          const req: ICreateUpdateScheduledPost = {
            scheduledTime: scheduledTime,
            postingEntityId: pinterestUserId,
            config: {
              boardId: pin.boardId,
              boardSection: pin.boardSection,
              cuids: pin.cuids,
              description: pin.description,
              title: pin.title,
              url: pin.destinationLink,
              '@type': UniversalScheduledPostConfigType.PinterestOrganicPin,
            } as IOrganicPinConfig,
          };

          return req;
        })
        .filter(isDefined);

      try {
        await universalScheduledPostsApi.createScheduledPosts(pinRequests);
        dispatch(actions.saveScheduledPinSucceeded());

        dispatch(
          toastNotificationsActions.addToastNotification(
            scheduleBulkPinsSucceededToast(pins.length, startScheduledTime),
          ),
        );

        return true;
      } catch (_e) {
        dispatch(
          actions.saveScheduledPinFailed(
            PostDetailsScheduleErrorMessages.RequestSchedulePinFailed,
          ),
        );
        return false;
      }
    },
);

export const swapDraftToScheduledPin = createThunk(
  (draftId: DraftId) =>
    async (dispatch, getState): Promise<boolean> => {
      const state = getState();

      const isSchedulingPost = getIsSchedulingPin(state);
      const isSavingDraftPost = getIsDraftingPin(state);

      // Return early if the post is already being saved / scheduled
      if (isSchedulingPost || isSavingDraftPost) {
        return false;
      }

      const didSchedulePost = await dispatch(
        validateAndSchedulePin({ shouldShowToast: false }),
      );

      if (!didSchedulePost) {
        // Error handling will come from validateAndSchedulePost
        return false;
      }

      const didDeleteDraft = await dispatch(
        deleteDraftPost(draftId, { shouldShowToast: false }),
      );

      if (didDeleteDraft) {
        dispatch(addSwapDraftToPostSuccessToast());
      } else {
        dispatch(addSwapDraftToPostFailureToast());
      }

      return didDeleteDraft;
    },
);

const deleteScheduledPin = createThunk((postableId: PostableId) => async () => {
  try {
    await deleteScheduledPosts([postableId]);
    return true;
  } catch (e) {
    console.error(
      getRequestErrorMessage(e) ??
        PostDetailsDraftErrorMessages.RequestDeletePinFailed,
    );
    return false;
  }
});

export const swapScheduledPinToDraft = createThunk(
  (postableId: PostableId) =>
    async (dispatch, getState): Promise<boolean> => {
      const state = getState();
      const isSchedulingPost = getIsSchedulingPin(state);
      const isSavingDraftPost = getIsDraftingPin(state);

      // Return early if the post is already being saved / scheduled
      if (isSchedulingPost || isSavingDraftPost) {
        return false;
      }

      const didSaveDraft = await dispatch(saveDraftSinglePin());

      if (!didSaveDraft) {
        // Error handling will come from saveDraft
        return false;
      }

      const didDeleteScheduledPost = await dispatch(
        deleteScheduledPin(postableId),
      );

      if (didDeleteScheduledPost) {
        dispatch(addSwapPostToDraftSuccessToast());
      } else {
        dispatch(addSwapPostToDraftFailureToast());
      }

      return didDeleteScheduledPost;
    },
);

export default reducer;
